import type { CharacterAxis } from '../schema/characterAxes';
import {
  DEFAULT_CHARACTER_FORWARD_AXIS,
  DEFAULT_CHARACTER_UP_AXIS,
} from '../schema/characterAxes';
import type { ChainMapping } from '../schema/mapping';
import { ROLE_HINTS } from '../schema/mapping';
import type { ScoreBreakdown, ScoringProfile, NamingMode } from './scoring';
import type { Check, CheckResult, EvaluationResult } from './checks';

// ═══════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════

export const AUX_PATTERNS: string[] = [
  'ik', '\uff29\uff2b', '\uff49\uff4b', 'ctrl', 'control', 'target', 'pole',
  'end', 'eff', 'helper', 'socket', 'goal', 'guard', 'piston', 'shock',
  'dummy', 'shadow', 'fx', 'nub', 'skirt', 'cape',
];

export const EXCLUDE_PATTERNS: string[] = [
  'twist',
  'roll_',
  '_roll',
  'corrective',
  '_xtra',
  'ik_', '_ik',
  'ikgoal', 'ikgoal_', '_ikgoal',
  'pole_ik', '_pole', 'polevector',
  'ik_foot', 'ik_hand', 'ik_weapon',
  'camera',
  'interaction',
  'center_of_mass',
  'com_',
  '_com',
  'root_motion',
  'pelvis_offset',
  'weapon_socket',
  'itempoint',
  'attach_',
  '_attach',
  'cloth_',
  '_cloth',
  'skirt_',
  '_skirt',
  'cape_',
  'ribbon_',
  '_ribbon',
  'ribbon',
  'belt',
  'buckle',
  'hair_',
  '_hair',
  'hair',
  'bodyhair',
  'emohair',
  'sideburn',
  'ponytail',
  'braid',
  'jiggle',
  'bounce',
  'physics_',
  'eye',
  'ojo',
  'oeil',
  'œil',
  'ear_',
  '_ear',
  'lobe',
  'jaw',
  'mandivula',
  'mandibula',
  'machoire',
  'mâchoire',
  'beak',
  'eyelid',
  'lid',
  'eyebrow',
  'tongue',
  'lengua',
  'langue',
  'teeth',
  'lip',
  'lips',
  'cheek',
  'nose',
  'brow',
  'nostril',
  'mouth',
  'corner',
  'comis',
  'wing_',
  '_wing',
  'feather',
  'fin_',
  'bow_',
  '_bow',
  'breast_',
  '_breast',
  'breast',
  'necklace',
  'strap',
  'backpack',
];

export const FINGER_PATTERNS: Record<string, string[]> = {
  Thumb: ['thumb', '\u89aa\u6307'],
  Index: ['index', '\u4eba\u6307'],
  Middle: ['middle', 'mid', '\u4e2d\u6307'],
  Ring: ['ring', '\u85ac\u6307'],
  Pinky: ['pinky', 'little', '\u5c0f\u6307'],
};

// ═══════════════════════════════════════════════════════════════════════
// CANDIDATE INFO
// ═══════════════════════════════════════════════════════════════════════

export type ChainResult = ChainMapping;

type EvidenceState = 'positive' | 'negative' | 'neutral';

export class CandidateInfo {
  boneName: string;
  score: number;
  checkResults: [string, string][];
  categoryResults: Record<string, [string, string, number, number][]>;
  categoryScores: Record<string, number>;
  evidence: Record<string, unknown>;

  constructor(fields: {
    boneName: string;
    score: number;
    checkResults?: [string, string][];
    categoryResults?: Record<string, [string, string, number, number][]>;
    categoryScores?: Record<string, number>;
    evidence?: Record<string, unknown>;
  }) {
    this.boneName = fields.boneName;
    this.score = fields.score;
    this.checkResults = fields.checkResults ?? [];
    this.categoryResults = fields.categoryResults ?? {};
    this.categoryScores = fields.categoryScores ?? {};
    this.evidence = fields.evidence ?? {};
  }

  formatEvidenceItems(): [EvidenceState, string][] {
    if (this.checkResults.length > 0) {
      return this.checkResults as [EvidenceState, string][];
    }
    return this._formatLegacyEvidence();
  }

  private _formatLegacyEvidence(): [EvidenceState, string][] {
    const items: [EvidenceState, string][] = [];

    const scoreState = (value: number): EvidenceState => {
      if (value >= 0.7) return 'positive';
      if (value < 0.5) return 'negative';
      return 'neutral';
    };

    if ('symmetry_score' in this.evidence) {
      const sym = this.evidence.symmetry_score as number;
      items.push([scoreState(sym), `Symmetry: ${(sym * 100).toFixed(0)}%`]);
    }

    if ('direction_score' in this.evidence) {
      const d = this.evidence.direction_score as number;
      items.push([scoreState(d), `Direction alignment: ${(d * 100).toFixed(0)}%`]);
    }

    if ('naming_match' in this.evidence) {
      if (this.evidence.naming_match) {
        const hints = (this.evidence.matched_hints as string[] | undefined) ?? [];
        if (hints.length > 0) {
          items.push(['positive', `Name matches: ${hints.join(', ')}`]);
        } else {
          items.push(['positive', 'Name matches role hints']);
        }
      } else {
        items.push(['negative', 'No naming match']);
      }
    }

    if ('chain_position' in this.evidence) {
      const pos = this.evidence.chain_position;
      const total = this.evidence.chain_length ?? '?';
      items.push(['neutral', `Chain position: ${pos}/${total}`]);
    }

    if ('lateral_spread' in this.evidence) {
      const spread = this.evidence.lateral_spread as number;
      items.push([scoreState(spread), `Lateral spread: ${(spread * 100).toFixed(0)}%`]);
    }

    if ('center_alignment' in this.evidence) {
      const align = this.evidence.center_alignment as number;
      items.push([scoreState(align), `Center alignment: ${(align * 100).toFixed(0)}%`]);
    }

    if ('parent_name' in this.evidence) {
      items.push(['neutral', `Parent: ${this.evidence.parent_name}`]);
    }

    return items;
  }

  getCategoryChecks(category: string): [string, string, number][] {
    if (!(category in this.categoryResults)) return [];
    return this.categoryResults[category].map(([state, reason, , weight]) => [state, reason, weight]);
  }

  hasCategory(category: string): boolean {
    return category in this.categoryResults && this.categoryResults[category].length > 0;
  }

  getCategoryScore(category: string): number {
    return this.categoryScores[category] ?? 0.0;
  }

  static fromEvaluationResult(
    boneName: string,
    evalResult: EvaluationResult,
  ): CandidateInfo {
    const categoryResults: Record<string, [string, string, number, number][]> = {};
    const categoryWeights: Record<string, number> = {};
    const categoryWeightedSums: Record<string, number> = {};

    for (const [check, result] of evalResult.results) {
      const cat = check.category;
      if (!categoryResults[cat]) {
        categoryResults[cat] = [];
        categoryWeights[cat] = 0;
        categoryWeightedSums[cat] = 0;
      }
      categoryResults[cat].push([result.state, result.reason, result.score, check.weight]);
      categoryWeights[cat] += check.weight;
      categoryWeightedSums[cat] += check.weight * result.score;
    }

    const categoryScores: Record<string, number> = {};
    for (const cat of Object.keys(categoryResults)) {
      const totalWeight = categoryWeights[cat];
      if (totalWeight > 0) {
        categoryScores[cat] = categoryWeightedSums[cat] / totalWeight;
      }
    }

    return new CandidateInfo({
      boneName,
      score: evalResult.score,
      checkResults: evalResult.toEvidenceItems(),
      categoryResults,
      categoryScores,
    });
  }

  static fromScoreBreakdown(
    boneName: string,
    _role: string,
    breakdown: ScoreBreakdown,
    parentName?: string | null,
    chainPosition?: number | null,
    chainLength?: number | null,
    matchedHints?: string[] | null,
    extraEvidence?: Record<string, unknown> | null,
  ): CandidateInfo {
    const evidence: Record<string, unknown> = {};

    if (breakdown.symmetry > 0) evidence.symmetry_score = breakdown.symmetry;
    if (breakdown.direction > 0) evidence.direction_score = breakdown.direction;

    if (breakdown.naming > 0.5) {
      evidence.naming_match = true;
      if (matchedHints) evidence.matched_hints = matchedHints;
    } else if (breakdown.naming < 0.3) {
      evidence.naming_match = false;
    }

    if (chainPosition != null) {
      evidence.chain_position = chainPosition;
      if (chainLength != null) evidence.chain_length = chainLength;
    }

    if (parentName) evidence.parent_name = parentName;
    if (extraEvidence) Object.assign(evidence, extraEvidence);

    return new CandidateInfo({
      boneName,
      score: breakdown.total,
      evidence,
    });
  }
}

// ═══════════════════════════════════════════════════════════════════════
// RECOGNIZER CONFIG
// ═══════════════════════════════════════════════════════════════════════

export class RecognizerConfig {
  weights: Record<string, number>;
  minConfidence: number;
  lengthEpsilon: number;
  namingHints: Record<string, string[]>;
  detectFingers: boolean;
  excludedBones: string[];
  autoExclude: boolean;
  scoringProfile: ScoringProfile | null;
  useAdaptiveScoring: boolean;
  detectedNamingMode: NamingMode | null;

  constructor(fields?: {
    weights?: Record<string, number>;
    minConfidence?: number;
    lengthEpsilon?: number;
    namingHints?: Record<string, string[]>;
    detectFingers?: boolean;
    excludedBones?: string[];
    autoExclude?: boolean;
    scoringProfile?: ScoringProfile | null;
    useAdaptiveScoring?: boolean;
    detectedNamingMode?: NamingMode | null;
  }) {
    this.weights = fields?.weights ?? {};
    this.minConfidence = fields?.minConfidence ?? 0.55;
    this.lengthEpsilon = fields?.lengthEpsilon ?? 1e-5;
    this.namingHints = fields?.namingHints ?? { ...ROLE_HINTS };
    this.detectFingers = fields?.detectFingers ?? true;
    this.excludedBones = fields?.excludedBones ?? [];
    this.autoExclude = fields?.autoExclude ?? true;
    this.scoringProfile = fields?.scoringProfile ?? null;
    this.useAdaptiveScoring = fields?.useAdaptiveScoring ?? true;
    this.detectedNamingMode = fields?.detectedNamingMode ?? null;
  }

  getEffectiveMinConfidence(): number {
    if (this.scoringProfile != null) {
      return this.scoringProfile.minConfidence;
    }
    return this.minConfidence;
  }

  shouldUseSpatialSideFallback(): boolean {
    if (this.scoringProfile != null) {
      return this.scoringProfile.useSpatialSideFallback;
    }
    return false;
  }

  shouldTrustSpatialPelvis(): boolean {
    if (this.scoringProfile != null) {
      return this.scoringProfile.trustSpatialPelvis;
    }
    return false;
  }
}

// ═══════════════════════════════════════════════════════════════════════
// MAPPING RESULT
// ═══════════════════════════════════════════════════════════════════════

export class MappingResult {
  roleToBone: Record<string, string>;
  roleToConfidence: Record<string, number>;
  chains: Record<string, string[]>;
  chainResults: Record<string, ChainResult>;
  warnings: string[];
  explain: Record<string, ScoreBreakdown>;
  presetName: string | null;
  presetId: string | null;
  detectedFeatures: unknown[];
  excludedBones: string[];
  candidates: Record<string, CandidateInfo[]>;
  roleToDetectionMethod: Record<string, string>;
  sideConvention: string | null;
  namingMode: string | null;
  /** Character forward direction in world space (mapping JSON: top-level `forward`). */
  forwardAxis: CharacterAxis;
  /** Character up direction in world space (mapping JSON: top-level `up`). */
  upAxis: CharacterAxis;
  /** Top-level keys from mapping JSON that Cartographer does not model (e.g. joint_offsets by bone ref). Round-tripped on import/export. */
  ioExtras: Record<string, unknown>;

  constructor(fields: {
    roleToBone: Record<string, string>;
    roleToConfidence: Record<string, number>;
    chains: Record<string, string[]>;
    chainResults: Record<string, ChainResult>;
    warnings: string[];
    explain: Record<string, ScoreBreakdown>;
    presetName?: string | null;
    presetId?: string | null;
    detectedFeatures?: unknown[];
    excludedBones?: string[];
    candidates?: Record<string, CandidateInfo[]>;
    roleToDetectionMethod?: Record<string, string>;
    sideConvention?: string | null;
    namingMode?: string | null;
    forwardAxis?: CharacterAxis;
    upAxis?: CharacterAxis;
    ioExtras?: Record<string, unknown>;
  }) {
    this.roleToBone = fields.roleToBone;
    this.roleToConfidence = fields.roleToConfidence;
    this.chains = fields.chains;
    this.chainResults = fields.chainResults;
    this.warnings = fields.warnings;
    this.explain = fields.explain;
    this.presetName = fields.presetName ?? null;
    this.presetId = fields.presetId ?? null;
    this.detectedFeatures = fields.detectedFeatures ?? [];
    this.excludedBones = fields.excludedBones ?? [];
    this.candidates = fields.candidates ?? {};
    this.roleToDetectionMethod = fields.roleToDetectionMethod ?? {};
    this.sideConvention = fields.sideConvention ?? null;
    this.namingMode = fields.namingMode ?? null;
    this.forwardAxis =
      fields.forwardAxis ?? DEFAULT_CHARACTER_FORWARD_AXIS;
    this.upAxis = fields.upAxis ?? DEFAULT_CHARACTER_UP_AXIS;
    this.ioExtras = fields.ioExtras ? { ...fields.ioExtras } : {};
  }

  getChainForBone(boneRef: string): string | null {
    for (const [chainName, refs] of Object.entries(this.chains)) {
      if (refs.includes(boneRef)) return chainName;
    }
    return null;
  }

  toJson(): Record<string, unknown> {
    return {
      role_to_bone: this.roleToBone,
      role_to_confidence: this.roleToConfidence,
      chains: this.chains,
      warnings: this.warnings,
      explain: Object.fromEntries(
        Object.entries(this.explain).map(([role, breakdown]) => [role, { ...breakdown }]),
      ),
    };
  }
}

// ═══════════════════════════════════════════════════════════════════════
// CANDIDATES
// ═══════════════════════════════════════════════════════════════════════

export class RoleCandidate {
  boneIdx: number;
  role: string;
  score: ScoreBreakdown;
  evidence: Record<string, unknown>;

  constructor(fields: {
    boneIdx: number;
    role: string;
    score: ScoreBreakdown;
    evidence?: Record<string, unknown>;
  }) {
    this.boneIdx = fields.boneIdx;
    this.role = fields.role;
    this.score = fields.score;
    this.evidence = fields.evidence ?? {};
  }

  get total(): number {
    return this.score.total;
  }
}

function defaultBreakdown(): ScoreBreakdown {
  return { topology: 0, symmetry: 0, direction: 0, lengthRatio: 0, naming: 0, depth: 0, total: 0 };
}

export class CandidateSet {
  candidates: Record<string, RoleCandidate[]> = {};

  add(candidate: RoleCandidate): void {
    if (!this.candidates[candidate.role]) {
      this.candidates[candidate.role] = [];
    }
    this.candidates[candidate.role].push(candidate);
  }

  addAll(candidates: RoleCandidate[]): void {
    for (const c of candidates) this.add(c);
  }

  getCandidates(role: string): RoleCandidate[] {
    return [...(this.candidates[role] ?? [])].sort((a, b) => b.total - a.total);
  }

  bestForRole(role: string): RoleCandidate | null {
    const sorted = this.getCandidates(role);
    return sorted.length > 0 ? sorted[0] : null;
  }

  allRoles(): string[] {
    return Object.keys(this.candidates);
  }

  resolveConflicts(rolePriority?: string[] | null): Record<string, RoleCandidate> {
    const assignedBones = new Set<number>();
    const result: Record<string, RoleCandidate> = {};

    let roles: string[];
    if (rolePriority) {
      roles = rolePriority;
    } else {
      roles = [...this.allRoles()].sort((a, b) => {
        const aScore = (this.bestForRole(a) ?? new RoleCandidate({ boneIdx: 0, role: a, score: defaultBreakdown() })).total;
        const bScore = (this.bestForRole(b) ?? new RoleCandidate({ boneIdx: 0, role: b, score: defaultBreakdown() })).total;
        return bScore - aScore;
      });
    }

    for (const role of roles) {
      for (const candidate of this.getCandidates(role)) {
        if (!assignedBones.has(candidate.boneIdx)) {
          result[role] = candidate;
          assignedBones.add(candidate.boneIdx);
          break;
        }
      }
    }

    return result;
  }

  explainSelection(role: string, selected: RoleCandidate): string {
    const allCandidates = this.getCandidates(role);
    if (allCandidates.length === 0) return `No candidates found for ${role}`;

    const lines = [`Selection for ${role}:`];
    for (let i = 0; i < allCandidates.length; i++) {
      const c = allCandidates[i];
      const marker = c.boneIdx === selected.boneIdx ? '→ ' : '  ';
      lines.push(
        `${marker}[${i + 1}] bone=${c.boneIdx} score=${c.total.toFixed(2)} ` +
        `(topo=${c.score.topology.toFixed(2)}, sym=${c.score.symmetry.toFixed(2)}, ` +
        `dir=${c.score.direction.toFixed(2)}, name=${c.score.naming.toFixed(2)})`,
      );
    }

    if (Object.keys(selected.evidence).length > 0) {
      lines.push(`Evidence: ${JSON.stringify(selected.evidence)}`);
    }

    return lines.join('\n');
  }
}
